import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Modal, ModalBody, Row, Spinner } from "reactstrap";
import { withTranslation } from "react-i18next";
import moment from "moment";
import TimePicker from "rc-time-picker";
import "rc-time-picker/assets/index.css";
import Text from "components/Text";
import CustomDropdown from "components/Dropdown";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";
import crossIcon from "assets/images/cross.svg";
import { getOfficesByOwnerAndUser } from "repositories/office-repository";
import { normalizeSchedulerOfficeId } from "accountOwner/pages/Scheduler/scheduler.function";
import {
  normalizeQuickEditOffices,
  resolveQuickEditSelectedOfficeId,
} from "./quickEdit.utils";
import styles from "./QuickEditModal.module.scss";

/**
 * Quick Edit popup for Weekly view by staff name.
 * Editable fields: Office, Date, Start Time, End Time.
 */
function QuickEditModal({
  t,
  isOpen,
  onClose,
  onSave,
  ownerId,
  userId,
  initialOfficeId,
  initialOfficeName,
  initialDate,
  initialStartTime,
  initialEndTime,
  isSaving = false,
}) {
  const [offices, setOffices] = useState([]);
  const [isLoadingOffices, setIsLoadingOffices] = useState(false);
  const [selectedOfficeId, setSelectedOfficeId] = useState(null);
  const [eventDate, setEventDate] = useState(moment().toDate());
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [errors, setErrors] = useState({});

  const applyOfficeSelection = useCallback(
    (officeList) => {
      const preferred = normalizeSchedulerOfficeId(initialOfficeId);
      const resolvedId = resolveQuickEditSelectedOfficeId({
        offices: officeList,
        preferredOfficeId: preferred,
        preferredOfficeName: initialOfficeName,
      });

      const hasResolvedInList =
        resolvedId &&
        officeList.some((office) => String(office.id) === String(resolvedId));

      if (preferred && !hasResolvedInList) {
        const nextOffices = [
          { id: preferred, name: initialOfficeName || "" },
          ...officeList.filter(
            (office) => String(office.id) !== String(preferred),
          ),
        ];
        setOffices(nextOffices);
        setSelectedOfficeId(preferred);
        return preferred;
      }

      setOffices(officeList);
      setSelectedOfficeId(resolvedId);
      return resolvedId;
    },
    [initialOfficeId, initialOfficeName],
  );

  const resetForm = useCallback(() => {
    const date = initialDate ? moment(initialDate).toDate() : moment().toDate();
    const start = initialStartTime ? moment(initialStartTime) : null;
    const end = initialEndTime ? moment(initialEndTime) : null;
    setEventDate(date);
    setStartTime(start?.isValid() ? start : null);
    setEndTime(end?.isValid() ? end : null);
    setSelectedOfficeId(normalizeSchedulerOfficeId(initialOfficeId));
    setErrors({});
  }, [initialDate, initialStartTime, initialEndTime, initialOfficeId]);

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen || !ownerId || !userId) {
      if (isOpen && !userId) {
        const preferred = normalizeSchedulerOfficeId(initialOfficeId);
        applyOfficeSelection(
          preferred ? [{ id: preferred, name: initialOfficeName || "" }] : [],
        );
      }
      return;
    }

    let isMounted = true;
    setIsLoadingOffices(true);

    getOfficesByOwnerAndUser(ownerId, userId)
      .then((officeList) => {
        if (!isMounted) return;
        applyOfficeSelection(normalizeQuickEditOffices(officeList));
      })
      .catch(() => {
        if (!isMounted) return;
        const preferred = normalizeSchedulerOfficeId(initialOfficeId);
        applyOfficeSelection(
          preferred ? [{ id: preferred, name: initialOfficeName || "" }] : [],
        );
      })
      .finally(() => {
        if (isMounted) setIsLoadingOffices(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    isOpen,
    ownerId,
    userId,
    initialOfficeId,
    initialOfficeName,
    applyOfficeSelection,
  ]);

  const officeOptions = useMemo(
    () =>
      offices.map((office) => ({
        id: office.id,
        name: office.name,
      })),
    [offices],
  );

  useEffect(() => {
    if (!isOpen || selectedOfficeId || !offices.length) return;
    const resolvedId = resolveQuickEditSelectedOfficeId({
      offices,
      preferredOfficeId: initialOfficeId,
      preferredOfficeName: initialOfficeName,
    });
    if (resolvedId) setSelectedOfficeId(resolvedId);
  }, [isOpen, offices, selectedOfficeId, initialOfficeId, initialOfficeName]);

  const selectedOfficeLabel = useMemo(() => {
    const match = offices.find(
      (office) => String(office.id) === String(selectedOfficeId),
    );
    return match?.name || initialOfficeName || t("noOffices");
  }, [offices, selectedOfficeId, initialOfficeName, t]);

  const getResolvedOfficeId = () =>
    selectedOfficeId ||
    resolveQuickEditSelectedOfficeId({
      offices,
      preferredOfficeId: initialOfficeId,
      preferredOfficeName: initialOfficeName,
    });

  const validate = (officeId) => {
    const nextErrors = {};
    if (!officeId) {
      nextErrors.office = t("form.errors.fieldrequired");
    }
    if (!eventDate || !moment(eventDate).isValid()) {
      nextErrors.date = t("form.errors.fieldrequired");
    }
    if (!startTime || !moment(startTime).isValid()) {
      nextErrors.startTime = t("form.errors.fieldrequired");
    }
    if (!endTime || !moment(endTime).isValid()) {
      nextErrors.endTime = t("form.errors.fieldrequired");
    }
    if (
      startTime &&
      endTime &&
      moment(endTime).isSameOrBefore(moment(startTime))
    ) {
      nextErrors.endTime = t("form.errors.endTimeShouldBeGreater");
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    const officeId = getResolvedOfficeId();
    if (officeId && officeId !== selectedOfficeId) {
      setSelectedOfficeId(officeId);
    }
    if (!validate(officeId) || isSaving) return;
    onSave({
      officeId,
      eventDate,
      startTime,
      endTime,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      className={`modal-dialog-centered new-group-modal modal-lg`}
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={onClose}>
        <img src={crossIcon} alt="close" />
      </span>

      <ModalBody className={styles.body}>
        <Text size="25px" marginBottom="24px" weight="500" color="#111b45">
          {t("scheduler.quickEdit")}
        </Text>

        <div className={`c-field ${styles.field}`}>
          <label>{t("offices")}</label>
          <div className={`custom-dropdown-only ${styles.officeDropdown}`}>
            {isLoadingOffices ? (
              <div className={styles.officeDropdownLoader}>
                <Spinner
                  animation="border"
                  size="sm"
                  className="loader-spinner"
                />
              </div>
            ) : null}
            <CustomDropdown
              options={officeOptions}
              selectedOption={selectedOfficeId}
              defaultValue={selectedOfficeLabel}
              selectOption={(id) => {
                setSelectedOfficeId(normalizeSchedulerOfficeId(id) ?? id);
                setErrors((prev) => ({ ...prev, office: undefined }));
              }}
              disabled={isLoadingOffices || isSaving}
            />
          </div>
          {errors.office ? (
            <span className="error-msg">{errors.office}</span>
          ) : null}
        </div>

        <div className={`c-field ${styles.field}`}>
          <label>{t("accountOwner.date")}</label>
          <div className="d-flex inputdate">
            <ReactDatePickerCommon
              dateFormat="dd-MM-yyyy"
              className="c-form-control"
              selected={eventDate}
              onChange={(date) => {
                setEventDate(date);
                setErrors((prev) => ({ ...prev, date: undefined }));
              }}
              disabled={isSaving}
            />
          </div>
          {errors.date ? (
            <span className="error-msg">{errors.date}</span>
          ) : null}
        </div>

        <Row>
          <Col xs="6">
            <div className={`c-field ${styles.field}`}>
              <label>{t("start")}</label>
              <TimePicker
                showSecond={false}
                placeholder="--"
                format="h:mm A"
                use12Hours
                className="event-time-picker"
                value={startTime}
                minuteStep={1}
                disabled={isSaving}
                onChange={(value) => {
                  setStartTime(value);
                  setErrors((prev) => ({
                    ...prev,
                    startTime: undefined,
                    endTime: undefined,
                  }));
                }}
              />
              {errors.startTime ? (
                <span className="error-msg">{errors.startTime}</span>
              ) : null}
            </div>
          </Col>
          <Col xs="6">
            <div className={`c-field ${styles.field}`}>
              <label>{t("end")}</label>
              <TimePicker
                showSecond={false}
                placeholder="--"
                format="h:mm A"
                use12Hours
                className="event-time-picker"
                value={endTime}
                minuteStep={1}
                disabled={isSaving}
                onChange={(value) => {
                  setEndTime(value);
                  setErrors((prev) => ({
                    ...prev,
                    endTime: undefined,
                  }));
                }}
              />
              {errors.endTime ? (
                <span className="error-msg">{errors.endTime}</span>
              ) : null}
            </div>
          </Col>
        </Row>

        <div className={styles.actions}>
          <button
            type="button"
            className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100"
            title={t("scheduler.saveChanges")}
            disabled={isSaving || isLoadingOffices}
            onClick={handleSave}
          >
            {isSaving ? (
              <Spinner
                animation="border"
                size="sm"
                className="loader-spinner"
              />
            ) : (
              t("scheduler.saveChanges")
            )}
          </button>
          <button
            type="button"
            className="button button-round button-border btn-mobile-link button-dark mb-md-3"
            title={t("cancel")}
            disabled={isSaving}
            onClick={onClose}
          >
            {t("cancel")}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(QuickEditModal);
